import type { Box3D } from 'box3d-web';
import { createBox3D } from 'box3d-web';
import {
  createDemolitionStructureAdapter,
  type HostedDemolitionSceneParticipant,
} from './demolitionStructureAdapter';
import { DestructionSceneRuntime } from '../destructionSceneRuntime';
import { createDestructionIdentityFactory, type WorldIdentity } from '../identities';
import { SharedDestructionWorld } from '../sharedDestructionWorld';
import type { DestructionSceneEventEnvelope } from '../destructionSceneRuntime';
import {
  DEFAULT_WEAPON_POWER,
  DEMO_FIXED_DT,
  DEMO_SUBSTEPS,
  setDemoTool,
  setWeaponPower,
  type DemoTool,
  type DemolitionRuntime,
} from '../../structures/demolition/demolitionPhysics';
import { isProfileId, type ProfileId } from '../../services/destructionProfiles';
import type { Vec3 } from '../../services/fracture/voronoiFracture';
import type { CanonicalDemolitionEventUnion } from '../../structures/demolition/demolitionEventRing';

/** The one page-owned scene authority retained across route remounts. */
export interface DemolitionPageSession {
  readonly shared: SharedDestructionWorld;
  readonly scene: DestructionSceneRuntime<CanonicalDemolitionEventUnion>;
  readonly adapter: HostedDemolitionSceneParticipant;
  readonly runtime: DemolitionRuntime;
  readonly structureKey: string;
  readonly profileId: ProfileId;
  readonly tool: DemoTool;
  readonly weaponPower: number;
  readonly disposed: boolean;
  advance(delta: number): number;
  drainEvents(): readonly DestructionSceneEventEnvelope<CanonicalDemolitionEventUnion>[];
  fireAt(target: Vec3, direction: Vec3, tool?: DemoTool, power?: number): void;
  impactAt(point: Vec3, speed?: number, direction?: Vec3): void;
  detonateAt(point: Vec3, power?: number): void;
  placeChargeAt(point: Vec3, power?: number): void;
  reset(): void;
  setTool(tool: DemoTool): void;
  setWeaponPower(power: number): void;
  replaceProfile(profileId: ProfileId): Promise<DemolitionRuntime>;
  dispose(): void;
}

const PAGE_NAMESPACE = 'demolition-page';

function copyVec3(value: Vec3, name: string): Vec3 {
  if (!value || ![value.x, value.y, value.z].every(Number.isFinite)) {
    throw new RangeError(`${name} must contain finite coordinates`);
  }
  return Object.freeze({ x: value.x, y: value.y, z: value.z });
}

function checkedTool(tool: DemoTool): DemoTool {
  if (!['cannon', 'bomb', 'spike', 'rubble', 'concussion'].includes(tool)) throw new RangeError('unsupported demolition tool');
  return tool;
}

function checkedPower(power: number): number {
  if (!Number.isFinite(power) || power <= 0) throw new RangeError('weapon power must be finite and positive');
  return power;
}

class RetainedDemolitionPageSession implements DemolitionPageSession {
  private readonly box3d: Box3D;
  readonly shared: SharedDestructionWorld;
  readonly scene: DestructionSceneRuntime<CanonicalDemolitionEventUnion>;
  private currentAdapter: HostedDemolitionSceneParticipant;
  private currentProfile: ProfileId;
  private currentTool: DemoTool = 'cannon';
  private currentPower = DEFAULT_WEAPON_POWER;
  private structureOrdinal = 0;
  private readonly ids;
  private readonly worldIdentity: WorldIdentity;
  private isDisposed = false;

  private constructor(
    box3d: Box3D,
    shared: SharedDestructionWorld,
    scene: DestructionSceneRuntime<CanonicalDemolitionEventUnion>,
    adapter: HostedDemolitionSceneParticipant,
    profileId: ProfileId,
    ids: ReturnType<typeof createDestructionIdentityFactory>,
    worldIdentity: WorldIdentity,
  ) {
    this.box3d = box3d;
    this.shared = shared;
    this.scene = scene;
    this.currentAdapter = adapter;
    this.currentProfile = profileId;
    this.ids = ids;
    this.worldIdentity = worldIdentity;
  }

  static async create(profileId: ProfileId): Promise<RetainedDemolitionPageSession> {
    const box3d = await createBox3D();
    const ids = createDestructionIdentityFactory({ namespace: PAGE_NAMESPACE, generation: 0 });
    const worldIdentity = ids.world(0);
    const shared = new SharedDestructionWorld(box3d, worldIdentity, {
      gravity: [0, -9.8, 0],
    });
    try {
      const ground = shared.ensureGround();
      const scene = new DestructionSceneRuntime<CanonicalDemolitionEventUnion>(shared, {
        fixedDt: DEMO_FIXED_DT,
        substeps: DEMO_SUBSTEPS,
      });
      const adapter = await RetainedDemolitionPageSession.createAdapter(shared, ids, worldIdentity, 0, profileId, ground.body);
      setDemoTool(adapter.runtime, 'cannon');
      setWeaponPower(adapter.runtime, DEFAULT_WEAPON_POWER);
      scene.attach(adapter);
      return new RetainedDemolitionPageSession(box3d, shared, scene, adapter, profileId, ids, worldIdentity);
    } catch (error) {
      try { shared.dispose(); } catch { /* preserve construction error */ }
      try { box3d.dispose(); } catch { /* preserve construction error */ }
      throw error;
    }
  }

  private static async createAdapter(
    shared: SharedDestructionWorld,
    ids: ReturnType<typeof createDestructionIdentityFactory>,
    world: WorldIdentity,
    ordinal: number,
    profileId: ProfileId,
    groundBody: Parameters<typeof createDemolitionStructureAdapter>[0]['groundBody'],
  ): Promise<HostedDemolitionSceneParticipant> {
    const structure = ids.structure(ordinal, world);
    const element = ids.element(0, structure);
    const layer = ids.layer(0, element);
    return createDemolitionStructureAdapter({
      shared,
      structureId: structure,
      elementId: element,
      layerId: layer,
      materialId: profileId,
      profileId,
      groundBody,
    });
  }

  get adapter(): HostedDemolitionSceneParticipant { return this.currentAdapter; }
  get runtime(): DemolitionRuntime { return this.currentAdapter.runtime; }
  get structureKey(): string { return this.currentAdapter.structureKey; }
  get profileId(): ProfileId { return this.currentProfile; }
  get tool(): DemoTool { return this.currentTool; }
  get weaponPower(): number { return this.currentPower; }
  get disposed(): boolean { return this.isDisposed; }

  advance(delta: number): number {
    this.assertLive();
    return this.scene.advance(delta);
  }

  drainEvents(): readonly DestructionSceneEventEnvelope<CanonicalDemolitionEventUnion>[] {
    this.assertLive();
    return this.scene.drainEvents();
  }

  fireAt(target: Vec3, direction: Vec3, tool = this.currentTool, power = this.currentPower): void {
    this.assertLive();
    this.scene.enqueueCommand({
      targetStructureKey: this.structureKey,
      type: 'fire',
      payload: { target: copyVec3(target, 'fire.target'), direction: copyVec3(direction, 'fire.direction'), tool: checkedTool(tool), power: checkedPower(power) },
    });
  }

  impactAt(point: Vec3, speed = 25, direction: Vec3 = { x: 0, y: 0, z: -1 }): void {
    this.assertLive();
    this.scene.enqueueCommand({
      targetStructureKey: this.structureKey,
      type: 'probe-impact',
      payload: { ...copyVec3(point, 'probe-impact.point'), speed, direction: copyVec3(direction, 'probe-impact.direction') },
    });
  }

  detonateAt(point: Vec3, power = this.currentPower): void {
    this.assertLive();
    this.scene.enqueueCommand({
      targetStructureKey: this.structureKey,
      type: 'detonate',
      payload: { ...copyVec3(point, 'detonate.point'), power: checkedPower(power) },
    });
  }

  placeChargeAt(point: Vec3, power = this.currentPower): void {
    this.assertLive();
    this.scene.enqueueCommand({
      targetStructureKey: this.structureKey,
      type: 'place-charge',
      payload: { ...copyVec3(point, 'place-charge.point'), power: checkedPower(power) },
    });
  }

  reset(): void {
    this.assertLive();
    this.scene.enqueueCommand({ targetStructureKey: this.structureKey, type: 'reset-structure', payload: {} });
  }

  setTool(tool: DemoTool): void {
    this.assertLive();
    const nextTool = checkedTool(tool);
    this.scene.enqueueCommand({ targetStructureKey: this.structureKey, type: 'set-tool', payload: { tool: nextTool } });
    this.currentTool = nextTool;
  }

  setWeaponPower(power: number): void {
    this.assertLive();
    const nextPower = checkedPower(power);
    this.scene.enqueueCommand({ targetStructureKey: this.structureKey, type: 'set-weapon-power', payload: { power: nextPower } });
    this.currentPower = nextPower;
  }

  async replaceProfile(profileId: ProfileId): Promise<DemolitionRuntime> {
    this.assertLive();
    if (profileId === this.currentProfile) return this.runtime;
    const oldAdapter = this.currentAdapter;
    const nextOrdinal = this.structureOrdinal + 1;
    const ground = this.shared.ensureGround();
    // Candidate creation and attach are intentionally completed before the
    // old structure is detached. No scene step occurs during this transaction.
    const candidate = await RetainedDemolitionPageSession.createAdapter(this.shared, this.ids, this.worldIdentity, nextOrdinal, profileId, ground.body);
    try {
      setDemoTool(candidate.runtime, this.currentTool);
      setWeaponPower(candidate.runtime, this.currentPower);
      this.scene.attach(candidate);
    } catch (error) {
      try { candidate.dispose(); } catch { /* preserve attach error */ }
      throw error;
    }
    this.scene.detach(oldAdapter);
    oldAdapter.dispose();
    this.currentAdapter = candidate;
    this.currentProfile = profileId;
    this.structureOrdinal = nextOrdinal;
    return candidate.runtime;
  }

  dispose(): void {
    if (this.isDisposed) return;
    let firstError: unknown;
    try { this.scene.dispose(); } catch (error) { firstError = error; }
    try { this.currentAdapter.dispose(); } catch (error) { if (firstError === undefined) firstError = error; }
    try { this.shared.dispose(); } catch (error) { if (firstError === undefined) firstError = error; }
    try { this.box3d.dispose(); } catch (error) { if (firstError === undefined) firstError = error; }
    this.isDisposed = true;
    if (retainedSession === this) retainedSession = null;
    if (firstError !== undefined) throw firstError;
  }

  private assertLive(): void {
    if (this.isDisposed) throw new Error('demolition page session used after dispose()');
  }
}

let retainedSession: RetainedDemolitionPageSession | null = null;
let retainedSessionPromise: Promise<RetainedDemolitionPageSession> | null = null;

/** Return the module-retained page session, creating it once on first use. */
export async function getDemolitionPageSession(profileId?: ProfileId): Promise<DemolitionPageSession> {
  if (profileId !== undefined && !isProfileId(profileId)) throw new RangeError(`unsupported demolition profile ${profileId}`);
  if (retainedSession !== null && !retainedSession.disposed) {
    if (profileId !== undefined && retainedSession.profileId !== profileId) await retainedSession.replaceProfile(profileId);
    return retainedSession;
  }
  if (retainedSessionPromise === null) {
    retainedSessionPromise = RetainedDemolitionPageSession.create(profileId ?? 'concrete')
      .then((session) => { retainedSession = session; return session; })
      .finally(() => { retainedSessionPromise = null; });
  }
  return retainedSessionPromise;
}

/** Explicit test/dev teardown; ordinary page unmounts intentionally retain it. */
export function disposeExistingDemolitionPageSession(): void {
  retainedSession?.dispose();
  retainedSession = null;
  retainedSessionPromise = null;
}
